import CloseIcon from "@mui/icons-material/Close";
import {
  Backdrop,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  Paper,
  TextField,
  useTheme,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import SkuCopyRow from "../../../../../../views/components/common/SkuCopyRow";

type AdjustableItem = {
  clientSku: string;
  productName?: string | null;
  title?: string | null;
  onHand?: number | null;
  reserved?: number | null;
  free?: number | null;
};

interface AdjustItemPopupProps {
  open: boolean;
  item: AdjustableItem | null;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (payload: { delta: number; note: string }) => Promise<void>;
}

export default function AdjustItemPopup({
  open,
  item,
  isSubmitting = false,
  onClose,
  onSubmit,
}: AdjustItemPopupProps) {
  const theme = useTheme();
  const [delta, setDelta] = useState("");
  const [note, setNote] = useState("");
  const [isSubmitLocked, setIsSubmitLocked] = useState(false);
  const isSubmitLockedRef = useRef(false);

  const submitInProgress = isSubmitting || isSubmitLocked;

  useEffect(() => {
    if (!open) return;
    setDelta("");
    setNote("");
    setIsSubmitLocked(false);
    isSubmitLockedRef.current = false;
  }, [open]);

  const handleSubmit = async () => {
    if (isSubmitLockedRef.current || submitInProgress) {
      return;
    }

    const parsedDelta = Number(delta);
    if (!Number.isFinite(parsedDelta) || parsedDelta === 0) {
      return;
    }
    if (!note.trim()) {
      return;
    }

    isSubmitLockedRef.current = true;
    setIsSubmitLocked(true);

    try {
      await onSubmit({
        delta: parsedDelta,
        note: note.trim(),
      });
    } catch (error) {
      console.error("Failed to submit inventory adjustment:", error);
    } finally {
      isSubmitLockedRef.current = false;
      setIsSubmitLocked(false);
    }
  };

  const handleClose = () => {
    if (submitInProgress) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
            position: "relative",
          },
        },
      }}
      sx={{ zIndex: 2000 }}
    >
      <Backdrop
        open={submitInProgress}
        sx={{
          position: "absolute",
          zIndex: (muiTheme) => muiTheme.zIndex.modal + 1,
          color: theme.palette.customColors.white,
          borderRadius: "16px",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box
        sx={{
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <Box>
            <CustomTypography variant="heading4" fontWeight={700}>
              Adjust Item
            </CustomTypography>
            <CustomTypography variant="body3" color="text.secondary">
              Update stock with a manual ledger note
            </CustomTypography>
          </Box>
          <IconButton onClick={handleClose} disabled={submitInProgress}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                <CustomTypography variant="body3" color="text.secondary">
                  SKU
                </CustomTypography>
                <Box sx={{ mt: 0.5 }}>
                  <SkuCopyRow sku={item?.clientSku} fontWeight={600} />
                </Box>
                <CustomTypography
                  variant="body3"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {item?.productName || item?.title || "—"}
                </CustomTypography>
                <Grid2 container spacing={2} sx={{ mt: 1.5 }}>
                  <Grid2 size={{ xs: 6, sm: 4 }}>
                    <CustomTypography variant="body3" color="text.secondary">
                      Available
                    </CustomTypography>
                    <CustomTypography variant="body2" fontWeight={600}>
                      {item?.free ?? 0}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 6, sm: 4 }}>
                    <CustomTypography variant="body3" color="text.secondary">
                      Reserved
                    </CustomTypography>
                    <CustomTypography variant="body2" fontWeight={600}>
                      {item?.reserved ?? 0}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 6, sm: 4 }}>
                    <CustomTypography variant="body3" color="text.secondary">
                      On hand
                    </CustomTypography>
                    <CustomTypography variant="body2" fontWeight={600}>
                      {item?.onHand ?? 0}
                    </CustomTypography>
                  </Grid2>
                </Grid2>
              </Paper>
            </Grid2>
            <Grid2 size={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  DELTA
                </CustomTypography>
                <CustomTypography variant="body3" color="text.secondary">
                  Use positive to add stock, negative to deduct stock
                </CustomTypography>
                <Box sx={{ mt: 1 }}>
                  <CustomTextField
                    type="number"
                    value={delta}
                    onChange={(e) => setDelta(e.target.value)}
                    width="100%"
                    placeholder="Enter adjustment delta"
                    disabled={submitInProgress}
                  />
                </Box>
              </Paper>
            </Grid2>
            <Grid2 size={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  NOTE
                </CustomTypography>
                <CustomTypography variant="body3" color="text.secondary">
                  This note will be recorded against the stock adjustment
                </CustomTypography>
                <TextField
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  fullWidth
                  placeholder="Enter adjustment note"
                  multiline
                  minRows={4}
                  disabled={submitInProgress}
                  sx={{
                    mt: 1,
                    "& .MuiInputBase-root": {
                      fontSize: "0.8rem",
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "0.8rem",
                    },
                  }}
                />
              </Paper>
            </Grid2>
            <Grid2
              size={12}
              display="flex"
              justifyContent="flex-end"
              gap={1.5}
              sx={{ pt: 1 }}
            >
              <AppButton
                label="Cancel"
                onClick={handleClose}
                variant="grayscale"
                disabled={submitInProgress}
              />
              <AppButton
                label="Submit"
                onClick={() => {
                  if (submitInProgress) return;
                  void handleSubmit();
                }}
                variant="primary"
                disabled={
                  submitInProgress ||
                  !delta ||
                  Number(delta) === 0 ||
                  !note.trim()
                }
                customStyles={{
                  pointerEvents: submitInProgress ? "none" : "auto",
                }}
              />
            </Grid2>
          </Grid2>
        </DialogContent>
      </Box>
    </Dialog>
  );
}
